import { PromiseResolvers } from '@nutrien/minesight-utility-module';

import { FeatureFlags } from '@/featureFlags/FeatureFlags';

import { Crumbs } from './bakery/Crumbs';
import { crumble as crumbleDatabase, Database } from './Database';
import { crumble as crumbleGoalResolvers, GoalResolvers } from './GoalResolvers';
import { crumble as crumbleReplication, Replication } from './Replication';
import { Task } from './Task';
import { crumble as crumbleTrackingEvent, TrackingEvent } from './TrackingEvent';

export interface State {
  readonly borerSelected: boolean;
  readonly cleaned: boolean;
  readonly currentTask: Task | undefined;
  readonly database: Database | undefined;
  readonly disabled: boolean;
  readonly featureFlags: FeatureFlags;
  readonly goalResolvers: GoalResolvers;
  readonly legacy: boolean;
  readonly nextTask: Task | undefined;
  readonly online: boolean;
  readonly replication: Replication | undefined;
  readonly trackingEvents: TrackingEvent[];
  readonly whenIdle: PromiseResolvers<void> | undefined;
}

export function describe(state: State) {
  if (state.currentTask) return state.currentTask.kind;
  if (state.nextTask) return `Next: ${state.nextTask.kind}`;
  if (state.disabled) return 'Disabled';
  if (state.replication) {
    if (state.replication.families.length > 0) {
      return 'Replicating (F)';
    }
    return 'Replicating';
  }
  if (!state.featureFlags.syncEnhancements && !state.database) return 'Legacy';
  if (!state.online) return 'Offline';
  if (state.cleaned) return 'Cleaned';
  if (!state.borerSelected) return 'No Borer';
  if (state.database) return 'Initialized';
  if (!state.whenIdle) return 'Idle';
  // We shouldn't hit this point.
  return 'Unknown';
}

export type StateCrumbs = Crumbs & {
  [K in keyof Omit<State, 'featureFlags' | 'sendBorerHeartbeat' | 'eventTracking'>]: unknown;
} & {
  canReplicate: boolean;
  description: string;
  upgraded: boolean;
};

export function canReplicate(state: State): boolean {
  const { allowSync } = state.featureFlags.msBorerDataSync;
  return !!state.database && !state.disabled && state.online && state.borerSelected && allowSync;
}

export function crumble(state: State): StateCrumbs {
  const {
    cleaned,
    currentTask,
    database,
    disabled,
    featureFlags,
    goalResolvers,
    legacy,
    nextTask,
    online,
    replication,
    borerSelected,
    whenIdle,
  } = state;
  return {
    borerSelected,
    canReplicate: canReplicate(state),
    cleaned,
    currentTask: currentTask?.kind || 'None',
    database: database ? crumbleDatabase(database) : 'None',
    description: describe(state),
    disabled,
    goalResolvers: crumbleGoalResolvers(goalResolvers),
    legacy,
    nextTask: nextTask?.kind || 'None',
    online,
    replication: replication ? crumbleReplication(replication) : 'None',
    upgraded: featureFlags.syncEnhancements,
    trackingEvents: state.trackingEvents.map(e => crumbleTrackingEvent(e)),
    whenIdle: whenIdle ? 'Present' : 'None',
  };
}
